/**
 * FollowButton Component
 * Handles follow/unfollow actions with loading states
 */
import React, { useState } from 'react';
import Button from '../UI/button';
import { socialService } from '../../services/api/socialService';
import { useAuth } from '../../contexts/AuthContext';

interface FollowButtonProps {
  userId: string;
  initialIsFollowing?: boolean;
  onFollowChange?: (isFollowing: boolean) => void;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export const FollowButton: React.FC<FollowButtonProps> = ({
  userId,
  initialIsFollowing = false,
  onFollowChange,
  size = 'md',
  className = ''
}) => {
  const { user } = useAuth();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [loading, setLoading] = useState(false);

  // Don't show button if viewing own profile
  if (!user || user._id === userId) {
    return null;
  }

  const handleToggleFollow = async () => {
    setLoading(true);
    try {
      if (isFollowing) {
        await socialService.unfollowUser(userId);
        setIsFollowing(false);
        onFollowChange?.(false);
      } else {
        await socialService.followUser(userId);
        setIsFollowing(true);
        onFollowChange?.(true);
      }
    } catch (error) {
      console.error('Failed to toggle follow:', error);
      // TODO: Show toast notification for error
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      onClick={handleToggleFollow}
      disabled={loading}
      variant={isFollowing ? 'outline-secondary' : 'primary'}
      size={size}
      className={className}
      style={{ minWidth: '100px' }}
    >
      {loading ? (
        <span
          className='spinner-border spinner-border-sm'
          role='status'
          aria-hidden='true'
        />
      ) : isFollowing ? (
        'Following'
      ) : (
        'Follow'
      )}
    </Button>
  );
};
